import type {
    ConversationMessage,
    ConversationParticipant,
    ConversationContact,
    Conversation,
    sendConversationMessage_Body,
} from '@/api';
import { uniqueId, minBy, maxBy } from 'lodash';
import { z } from 'zod';
import { AxiosError } from 'axios';
import {
    ConversationMessageCreated,
    ConversationMessageUpdated,
    ConversationParticipantAdded,
    ConversationParticipantRemoved,
    ConversationParticipantUpdated,
    ConversationUpdated,
} from '@/api/events';

type Events = {
    /**
     * Emitted when the state of the conversation is reset
     */
    'state-reset': [];
    'message-sent': [temporaryUniqueId: string, message: z.infer<typeof sendConversationMessage_Body>];
    'message-created': [message: ConversationMessage, temporaryUniqueId?: string];
    'message-deleted': [message: ConversationMessage];
    'message-failed': [temporaryUniqueId: string, error?: string];
    'messages-loaded': [message: ConversationMessage[], hasMore?: boolean];
    'message-updated': [message: ConversationMessage];
    'user-typing': [name: string];
    'user-became-active': [name: string];
    'user-became-inactive': [name: string];
    'participant-added': [participant: ConversationParticipant];
    'participant-removed': [participant: ConversationParticipant];
    'participant-updated': [participant: ConversationParticipant];
    'participants-loaded': [participants: ConversationParticipant[]];
    'conversation-loaded': [conversation: Conversation];
    'conversation-updated': [properties: Conversation];
    'conversation-id-missing': [];
    'contacts-loaded': [contacts: ConversationContact[]];
    error: [error: any];
};

export const useConversation = (conversationId: Ref<number | undefined>) => {
    const events: {
        [K in keyof Events]?: (...args: Events[K]) => void;
    } = {};

    const on = <E extends keyof Events>(e: E, cb: (...args: Events[E]) => void) => {
        events[e] = cb as any;
    };
    const off = <E extends keyof Events>(e: E) => {
        delete events[e];
    };

    const emit = <E extends keyof Events>(e: E, ...args: Events[E]) => {
        events[e]?.(...args);
    };

    // #region Init

    const lastFetchedConversation = ref<number>();

    const refresh = async () => {
        if (conversationId.value == null) {
            emit('conversation-id-missing');
            return;
        }
        try {
            const [conversation] = await Promise.all([
                api.getConversation({
                    params: { conversation: conversationId.value },
                    queries: {
                        include: ['recent_messages', 'participants', 'company_branches', 'companies'],
                    },
                }),
            ]);
            lastFetchedConversation.value = conversation.data.id;
            emit('state-reset');
            emit('conversation-loaded', conversation.data);
            if (conversation.data.participants) {
                emit('participants-loaded', conversation.data.participants);
            } else {
                await loadParticipants();
            }
            if (conversation.data.recent_messages) {
                emit(
                    'messages-loaded',
                    conversation.data.recent_messages.messages,
                    conversation.data.recent_messages.has_more,
                );
            } else {
                await loadMessages();
            }
        } catch (e) {
            console.error(e);
            emit('error', e);
        }
    };

    watch(
        conversationId,
        id => {
            if (id !== lastFetchedConversation.value) {
                refresh();
            }
        },
        { immediate: true },
    );

    // #endregion

    // #region Actions

    const sendMessage = async (body: z.infer<typeof sendConversationMessage_Body>) => {
        if (conversationId.value == null) {
            emit('conversation-id-missing');
            return;
        }
        const temporaryUniqueId = uniqueId();

        try {
            emit('message-sent', temporaryUniqueId, body);
        } catch (e) {
            console.error(e);
        }

        try {
            const { data } = await api.sendConversationMessage(body, {
                params: { conversation: conversationId.value },
            });
            emit('message-created', data, temporaryUniqueId);
            return data;
        } catch (e) {
            if (e instanceof AxiosError && e.response?.status === 400) {
                const parsed = z
                    .object({
                        message: z.string(),
                    })
                    .safeParse(e.response.data);
                if (parsed.success) {
                    emit('message-failed', temporaryUniqueId, parsed.data.message);
                    return;
                }
            }
            emit('message-failed', temporaryUniqueId);
            return;
        }
    };

    const { isLoading: isLoadingMessages, withLoading: withLoadingMessages } = useLoading();
    const loadMessages = withLoadingMessages(
        async (
            query: ConversationMessage[] | { limit?: number; beforeId?: number | null } = { limit: 50, beforeId: null },
        ) => {
            if (conversationId.value == null) {
                emit('conversation-id-missing');
                return;
            }
            try {
                const queries = Array.isArray(query) ? { limit: 50, before_id: minBy(query, m => m.id)?.id } : query;
                console.log(queries);
                const { data, has_more } = await api.getConversationMessages({
                    params: { conversation: conversationId.value },
                    queries: { ...queries, include: ['attachments'] },
                });
                emit('messages-loaded', data, has_more);
            } catch (e) {
                console.error(e);
                emit('error', e);
            }
        },
    );

    const { isLoading: isLoadingParticipants, withLoading: withLoadingParticipants } = useLoading();
    const loadParticipants = async () => {
        if (conversationId.value == null) {
            emit('conversation-id-missing');
            return;
        }
        try {
            const { data } = await api.getConversationParticipants({
                params: { conversation: conversationId.value },
            });
            emit('participants-loaded', data);
        } catch (e) {
            console.error(e);
            emit('error', e);
        }
    };

    const { isLoading: isAddingParticipant, withLoading: withAddingParticipant } = useLoading();
    const addParticipant = withAddingParticipant(async (userId: number) => {
        if (conversationId.value == null) {
            emit('conversation-id-missing');
            return;
        }
        try {
            const { data } = await api.addConversationParticipant(
                {
                    user_id: userId,
                },
                {
                    params: { conversation: conversationId.value },
                },
            );
            emit('participant-added', data);
        } catch (e) {
            console.error(e);
            emit('error', e);
        }
    });

    const { isLoading: isLoadingContacts, withLoading: withLoadingContacts } = useLoading();
    const loadContacts = withLoadingContacts(async () => {
        if (conversationId.value == null) {
            emit('conversation-id-missing');
            return;
        }
        try {
            const { data } = await api.getConversationContacts({
                params: { conversation: conversationId.value },
            });
            emit('contacts-loaded', data);
        } catch (e) {
            console.error(e);
            emit('error', e);
        }
    });

    const removeParticipant = async (userId: number) => {
        if (conversationId.value == null) {
            emit('conversation-id-missing');
            return;
        }
        try {
            const { data } = await api.removeConversationParticipant(undefined, {
                params: { conversation: conversationId.value, participant: userId },
            });
            emit('participant-removed', data);
        } catch (e) {
            console.error(e);
            emit('error', e);
        }
    };

    const markRead = async (messageId: number | { me: ConversationParticipant; messages: ConversationMessage[] }) => {
        try {
            const id = typeof messageId === 'number' ? messageId : maxBy(messageId.messages, m => m.id)?.id;
            if (
                id == null ||
                (typeof messageId !== 'number' && messageId.me.read_upto_id != null && messageId.me.read_upto_id >= id)
            ) {
                return;
            }
            console.log(id, messageId);
            const { data } = await api.markConversationMessageRead(undefined, {
                params: { message: id },
            });
            emit('participant-updated', data);
        } catch (e) {
            console.error(e);
        }
    };

    const actions = {
        sendMessage,
        loadMessages,
        refresh,
        loadParticipants,
        addParticipant,
        removeParticipant,
        markRead,
        loadContacts,
    };

    // #endregion

    // #region Events

    const conversationChannel = computed(() =>
        lastFetchedConversation.value == null ? undefined : `conversations.${lastFetchedConversation.value}`,
    );

    useChannel(conversationChannel, ConversationUpdated, data => {
        if (data?.data != null && data.data.id === lastFetchedConversation.value) {
            emit('conversation-updated', data.data);
        }
    });

    useChannel(conversationChannel, ConversationMessageCreated, data => {
        if (data?.data != null && data.data.conversation_id === lastFetchedConversation.value) {
            emit('message-created', data.data);
        }
    });
    useChannel(conversationChannel, ConversationMessageUpdated, data => {
        if (data?.data != null && data.data.conversation_id === lastFetchedConversation.value) {
            emit('message-updated', data.data);
        }
    });
    useChannel(conversationChannel, ConversationParticipantAdded, data => {
        if (data?.data != null && data.data.conversation_id === lastFetchedConversation.value) {
            emit('participant-added', data.data);
        }
    });

    useChannel(conversationChannel, ConversationParticipantRemoved, data => {
        if (data?.data != null && data.data.conversation_id === lastFetchedConversation.value) {
            emit('participant-removed', data.data);
        }
    });

    useChannel(conversationChannel, ConversationParticipantUpdated, data => {
        if (data?.data != null && data.data.conversation_id === lastFetchedConversation.value) {
            emit('participant-updated', data.data);
        }
    });

    // #endregion

    return { on, off, actions, isLoadingMessages, isLoadingContacts, isLoadingParticipants, isAddingParticipant };
};
